// 118 — Solicitud de Sangre (Anexo 1 MSP, SNS-MSP/HCU-form.118/2021). Fase 7, Tanda 5: bloques B-F y pie del papel
// (servicio y prioridad con dos diagnósticos CIE, antecedentes transfusionales, laboratorio actual, 14 componentes con
// número y letras, texto legal y toma de muestra). Campos y decisiones en `bloques118Datos.ts`. El bloque A lo
// resuelve `EncabezadoMspCampos`; el profesional que firma lo arma el servidor en el PDF.
import { useMedplum } from '@medplum/react-hooks';
import type { ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { Bloque, CamposSimples } from './camposSimples';
import { codeDeDiagnostico, SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_ANTECEDENTES_118,
  CAMPOS_LABORATORIO_118,
  CAMPOS_OTRO_COMPONENTE_118,
  CAMPOS_SERVICIO_118,
  CAMPOS_TOMA_MUESTRA_118,
  erroresObligatorios118,
  COLUMNAS_COMPONENTES_118,
  estadoInicial118,
  FILAS_COMPONENTES_118,
  paresDe118,
  prioridadFhir118,
  TEXTO_LEGAL_118,
  textoSolicitud118,
  type Estado118,
} from './bloques118Datos';

export function Formulario118({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado118>(() => estadoInicial118());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '118', t('formularios.f118.titulo'), { servicio: estado.servicio.servicio });

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const errores = erroresObligatorios118(estado);
      if (errores.length > 0) throw new Error(errores.join(' '));
      const ahora = new Date();
      const dx = [estado.diagnosticoTransfusion, estado.diagnosticoPrincipal].filter((d) => d.texto.trim()).map((d) => codeDeDiagnostico(d));
      await medplum.createResource<ServiceRequest>({
        resourceType: 'ServiceRequest',
        status: 'active',
        intent: 'order',
        priority: prioridadFhir118(estado.servicio.prioridad),
        category: [{ text: 'Banco de sangre' }],
        code: { text: textoSolicitud118(estado) },
        subject,
        encounter,
        authoredOn: ahora.toISOString(),
        reasonCode: dx.length ? dx : undefined,
      });
      onGuardado(paresDe118(estado, ahora), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f118.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f118.servicioTitulo')}>
        <SelectorDiagnosticoCie10 label={t('formularios.f118.diagnosticoPrincipal')} value={estado.diagnosticoPrincipal} onChange={(v) => setEstado({ ...estado, diagnosticoPrincipal: v })} />
        <CamposSimples prefijoId="f118-srv" campos={CAMPOS_SERVICIO_118} valores={estado.servicio} columnas={3} onChange={(v) => setEstado({ ...estado, servicio: v })} />
        <SelectorDiagnosticoCie10 label={t('formularios.f118.diagnosticoTransfusion')} value={estado.diagnosticoTransfusion} onChange={(v) => setEstado({ ...estado, diagnosticoTransfusion: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f118.antecedentesTitulo')}>
        <CamposSimples prefijoId="f118-ant" campos={CAMPOS_ANTECEDENTES_118} valores={estado.antecedentes} columnas={2} onChange={(v) => setEstado({ ...estado, antecedentes: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f118.laboratorioTitulo')}>
        <CamposSimples prefijoId="f118-lab" campos={CAMPOS_LABORATORIO_118} valores={estado.laboratorio} columnas={4} onChange={(v) => setEstado({ ...estado, laboratorio: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f118.componentesTitulo')}>
        <TablaConColumnas filas={FILAS_COMPONENTES_118} columnas={COLUMNAS_COMPONENTES_118} estado={estado.componentes} onChange={(c) => setEstado({ ...estado, componentes: c })} />
        <CamposSimples prefijoId="f118-otro" campos={CAMPOS_OTRO_COMPONENTE_118} valores={estado.otroComponente} columnas={2} onChange={(v) => setEstado({ ...estado, otroComponente: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f118.pruebasTitulo')}>
        <p className="text-xs text-hce-muted">{TEXTO_LEGAL_118}</p>
      </Bloque>

      <Bloque titulo={t('formularios.f118.tomaMuestraTitulo')}>
        <CamposSimples prefijoId="f118-toma" campos={CAMPOS_TOMA_MUESTRA_118} valores={estado.tomaMuestra} columnas={3} onChange={(v) => setEstado({ ...estado, tomaMuestra: v })} />
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
